import React from 'react';
import { connect } from 'react-redux';
import { Box, Grid, GridItem } from '@drone-js/core';
import { killEvent } from 'core/utils/domUtils';
import { StickyFooter } from 'component/common/StickyFooter/StickyFooter';
import {
    mapDispatchToProps,
    mapStateToProps,
    OfferCardFooterProps,
    OfferCardFooterType,
} from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardBookButtonSection/OfferCardFooter.selectors';
import { OfferNotificationType } from 'component/page/detail/store/availabilityIndex.types';
import { OfferCardFooterPriceContainer } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardBookButtonSection/OfferCardFooterPriceContainer';
import { ActivityOfferToken } from 'core/type/Details.types';
import { scrollToTargetAdjusted } from 'core-framework/util/Dom';
import { CARD_EXPAND_TIME_OFFSET, TAB_HEADER_OFFSET } from 'component/common/DetailInViewWrapper/navigateToHashWithTabBarOffset';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesBookingOptionsBookButtonClickEventActivitiesFieldsV3,
    ActivitiesBookingOptionsUpdateButtonClickEventActivitiesFieldsV2,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesBookingOptionsBookButtonClickEventActivitiesManifestV3,
    ActivitiesBookingOptionsUpdateButtonClickEventActivitiesManifestV2,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { getDateInYmdFormat } from 'core-framework/util/datetime/date';
import ButtonGroup from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardBookButtonSection/ButtonGroup';

const OfferCardFooter: React.FC<OfferCardFooterProps> = (props) => {
    const { bookingFormUrl, isCartEligible, isLoading, selectedOffer, isSearchCriteriaUpdated } = props;
    const shouldFocusOnBookButton = React.useRef<boolean>(false);
    const isShowDateChipNotification = !props.isOfferDateChipSelected;
    const isShowOfferNotification = !props.isAllOfferSpecSelected;
    const isShowNotification = isShowDateChipNotification || isShowOfferNotification;
    const isBoxedFooterType = props.footerType === OfferCardFooterType.Boxed;
    const isStickyFooterType = props.footerType === OfferCardFooterType.Sticky;
    const isSummaryCardType = props.footerType === OfferCardFooterType.SummaryCard;
    const contentJustification = isBoxedFooterType ? 'flex-end' : isCartEligible || (isShowNotification && !isSearchCriteriaUpdated) ? 'space-between' : 'flex-end';
    const navigationHash = `#available-option-card-${props.offerIndex}`;
    const activityId = props.selectedOffer.offer?.activityId;

    const handleOfferNotificationCallback = () => {
        if (isShowDateChipNotification) props.setOfferNotification({ [OfferNotificationType.DateChip]: true });
        if (isShowOfferNotification) props.setOfferNotification({ [OfferNotificationType.OptionSpec]: true });

        if (props.expandFirstOfferCardOnDetailsLanding) {
            setTimeout(() => {
                scrollToTargetAdjusted(navigationHash, TAB_HEADER_OFFSET);
            }, CARD_EXPAND_TIME_OFFSET);
        }
    };

    const onUpdateSelected = (e: React.MouseEvent) => {
        killEvent(e);
        if (!isLoading) {
            trackElementEvent<ActivitiesBookingOptionsUpdateButtonClickEventActivitiesFieldsV2>(
                ActivitiesBookingOptionsUpdateButtonClickEventActivitiesManifestV2.ids,
                {},
                {
                    action_element_index: props.offerIndex + 1,
                    travel_date: getDateInYmdFormat(props.selectedDate),
                    activity_id: activityId,
                },
            );
        }
        if (isShowNotification) {
            handleOfferNotificationCallback();
            return;
        }
        const offerToken: ActivityOfferToken = props.offerOptionToken
            ? { activityToken: props.offerOptionToken, enableOfferFilter: true }
            : { activityToken: props.activityTokenFromState, enableOfferFilter: false };
        props.updateOffer(offerToken);
        shouldFocusOnBookButton.current = true;
    };

    const onOptionSelected = (e: React.MouseEvent) => {
        killEvent(e);
        if (!isLoading) {
            const offer = selectedOffer.offer;
            trackElementEvent<ActivitiesBookingOptionsBookButtonClickEventActivitiesFieldsV3>(
                ActivitiesBookingOptionsBookButtonClickEventActivitiesManifestV3.ids,
                {},
                {
                    action_element_index: props.offerIndex + 1,
                    activity_id: activityId,
                    offer_id: offer?.offerId || 0,
                    travel_date: getDateInYmdFormat(props.selectedDate),
                },
            );
        }
        if (isShowNotification) {
            handleOfferNotificationCallback();
            return;
        }
        props.navigateToBookingForm(bookingFormUrl, props.isWebView, props.isCartMigrationEnabled, props.pageTypeId, props.shouldSendCartMigrationFallbackMeasurement);
    };

    const FooterContainer = React.useMemo(() => (isStickyFooterType ? StickyFooter : Box), [isStickyFooterType]);
    const elementName = 'booking-options-footer';

    return (
        <FooterContainer data-testid={elementName} elementName={elementName}>
            <div className={props.isDetailPageForSimCard && !isStickyFooterType ? '' : 'ContentArea'}>
                <Grid spacing="12">
                    <GridItem sm={12}>
                        <Grid spacing="12">
                            <GridItem sm={12}>
                                <OfferCardFooterPriceContainer {...props} />
                            </GridItem>
                            <GridItem sm={12}>
                                <Box
                                    display="flex"
                                    justifyContent={contentJustification}
                                    flexDirection={isSummaryCardType ? 'column' : undefined}
                                    gap={isSummaryCardType ? '8' : undefined}
                                    pt={isSummaryCardType ? '16' : undefined}
                                >
                                    <ButtonGroup {...props} isShowNotification={isShowNotification} onOptionSelected={onOptionSelected} onUpdateSelected={onUpdateSelected} />
                                </Box>
                            </GridItem>
                        </Grid>
                    </GridItem>
                </Grid>
            </div>
        </FooterContainer>
    );
};
OfferCardFooter.displayName = 'OfferCardBookButtonSection';

export default connect(mapStateToProps, mapDispatchToProps)(OfferCardFooter);
